import React from "react";
import FileSaver from "file-saver";
import { saveAs } from "../../../utils/fileSaver";
import {
  buildQRExportZip,
  drawQRSingle,
  loadQRLogoImage,
} from "../../../utils/equipmentQRExportUtils";
import type { QRExportImage } from "../../../utils/equipmentQRExportUtils";
import type {
  Equipment,
  EquipmentFilters,
  EquipmentModel,
  EquipmentPrefilter,
  EquipmentSearchRequest,
  FilterCriteria,
} from "../../../api/equipment/Equipment.typed";
import type { ShowAlertFn } from "../../../contexts/app/notificationToast";
import {
  buildEquipmentsCSV,
  buildEquipmentsPDF,
  fetchAllFilteredEquipments,
} from "../../../utils/equipmentExport";

export type EquipmentExportFormat = "csv" | "pdf";

interface UseEquipmentExportsArgs {
  selectedModel: EquipmentModel | undefined;
  equipments: Equipment[];
  qrMap: Record<string, string>;
  selectedIds: string[];
  showAlert: ShowAlertFn;
  logoQrCodeUrl: string | undefined;
  searchCriteria: FilterCriteria[];
  searchLogic: "AND" | "OR";
  activePrefilter: EquipmentPrefilter | null;
  sortBy: string | undefined;
  sortOrder: "ASC" | "DESC" | undefined;
  regenerateEquipmentQRCode: (id: string, bg: string) => Promise<unknown>;
  searchEquipmentsByModelID: (
    req: EquipmentSearchRequest,
  ) => Promise<{ equipments: Equipment[]; total: number; pages: number } | undefined>;
  reloadCurrentPage: () => void | Promise<void>;
}

export function useEquipmentExports({
  selectedModel,
  equipments,
  qrMap,
  selectedIds,
  showAlert,
  logoQrCodeUrl,
  searchCriteria,
  searchLogic,
  activePrefilter,
  sortBy,
  sortOrder,
  regenerateEquipmentQRCode,
  searchEquipmentsByModelID,
  reloadCurrentPage,
}: UseEquipmentExportsArgs) {
  const [exportOpen, setExportOpen] = React.useState(false);
  const [exportAttrIds, setExportAttrIds] = React.useState<string[]>([]);
  const [exportEquipmentIds, setExportEquipmentIds] = React.useState<string[]>([]);
  const [regenOpen, setRegenOpen] = React.useState(false);
  const [regenEquipmentIds, setRegenEquipmentIds] = React.useState<string[]>([]);
  const [exporting, setExporting] = React.useState(false);

  const openSelectionExport = React.useCallback(() => {
    if (selectedIds.length === 0) return;
    setExportEquipmentIds([...selectedIds]);
    setExportOpen(true);
  }, [selectedIds]);

  const openExportForEquipment = React.useCallback((id: string) => {
    if (!id) return;
    setExportEquipmentIds([id]);
    setExportOpen(true);
  }, []);

  const closeExportModal = React.useCallback(() => {
    setExportOpen(false);
    setExportEquipmentIds([]);
  }, []);

  const toggleExportAttr = React.useCallback((attrId: string) => {
    setExportAttrIds((prev) =>
      prev.includes(attrId) ? prev.filter((id) => id !== attrId) : [...prev, attrId],
    );
  }, []);

  const openSelectionRegen = React.useCallback(() => {
    if (selectedIds.length === 0) return;
    setRegenEquipmentIds([...selectedIds]);
    setRegenOpen(true);
  }, [selectedIds]);

  const openRegenForEquipment = React.useCallback((id: string) => {
    if (!id) return;
    setRegenEquipmentIds([id]);
    setRegenOpen(true);
  }, []);

  const closeRegenModal = React.useCallback(() => {
    setRegenOpen(false);
    setRegenEquipmentIds([]);
  }, []);

  const exportQRCodes = React.useCallback(
    async (options?: { filenameAttrId?: string }) => {
      if (!selectedModel || exportEquipmentIds.length === 0) return;
      const selectedEquipments = (equipments || []).filter(
        (e) => e.ID && exportEquipmentIds.includes(e.ID),
      );
      let imgs: QRExportImage[];
      try {
        imgs = await Promise.all(
          selectedEquipments.map(
            (eq) =>
              new Promise<QRExportImage>((resolve, reject) => {
                const src = qrMap[eq.ID!];
                if (!src) {
                  reject(new Error("missing_qr"));

                  return;
                }
                const img = new Image();
                img.onload = () => resolve({ img, eq });
                img.onerror = () => reject(new Error("qr_load_failed"));
                img.src = src;
              }),
          ),
        );
      } catch {
        showAlert("Impossible de charger un QR pour l'export.", "danger");
        setExportOpen(false);

        return;
      }

      const logoImg = await loadQRLogoImage(logoQrCodeUrl);
      const singles = imgs.map(({ img, eq }) =>
        drawQRSingle(img, eq, exportAttrIds, options?.filenameAttrId, logoImg),
      );
      if (singles.length === 0) {
        setExportOpen(false);

        return;
      }
      if (singles.length === 1) {
        const { dataUrl, baseName } = singles[0]!;
        saveAs(dataUrl, baseName);
      } else {
        const blob = await buildQRExportZip(singles);
        saveAs(blob, "qrcodes_export.zip");
      }
      setExportOpen(false);
    },
    [selectedModel, exportEquipmentIds, equipments, qrMap, exportAttrIds, showAlert, logoQrCodeUrl],
  );

  const regenerateSelectedQRCodes = React.useCallback(
    async (bg: string) => {
      if (regenEquipmentIds.length === 0) return;
      try {
        for (const id of regenEquipmentIds) {
          await regenerateEquipmentQRCode(id, bg);
        }
        await reloadCurrentPage();
      } finally {
        setRegenOpen(false);
      }
    },
    [regenEquipmentIds, regenerateEquipmentQRCode, reloadCurrentPage],
  );

  const exportFilteredList = React.useCallback(
    async (format: EquipmentExportFormat) => {
      if (!selectedModel?.ID) return;
      setExporting(true);
      try {
        const filters: EquipmentFilters | undefined =
          searchCriteria.length > 0 ? { Criteria: searchCriteria, Logic: searchLogic } : undefined;

        const all = await fetchAllFilteredEquipments({
          modelId: selectedModel.ID,
          filters,
          prefilter: activePrefilter,
          sortBy,
          sortOrder,
          search: async (req) => {
            const result = await searchEquipmentsByModelID(req);

            return result ?? { equipments: [], total: 0, pages: 0 };
          },
        });

        if (all.length === 0) {
          showAlert("Aucun équipement à exporter avec les filtres actuels.", "warning");

          return;
        }

        const filterSummary =
          [
            activePrefilter?.Name,
            searchCriteria.length > 0
              ? `${searchCriteria.length} critère${searchCriteria.length > 1 ? "s" : ""}`
              : null,
          ]
            .filter(Boolean)
            .join(" • ") || undefined;

        const baseName = `${selectedModel.Name || "equipements"}`.replace(/[^\w-]+/g, "_");
        if (format === "csv") {
          const blob = buildEquipmentsCSV(selectedModel, all);
          FileSaver.saveAs(blob, `${baseName}.csv`);
        } else {
          const blob = buildEquipmentsPDF(selectedModel, all, { filtersSummary: filterSummary });
          FileSaver.saveAs(blob, `${baseName}.pdf`);
        }
      } catch (err) {
        showAlert("L'export a échoué. Réessayez.", "danger");
        console.error("equipment export failed", err);
      } finally {
        setExporting(false);
      }
    },
    [
      selectedModel,
      searchCriteria,
      searchLogic,
      activePrefilter,
      sortBy,
      sortOrder,
      searchEquipmentsByModelID,
      showAlert,
    ],
  );

  return {
    exportOpen,
    exportAttrIds,
    regenOpen,
    exporting,
    openSelectionExport,
    openExportForEquipment,
    closeExportModal,
    toggleExportAttr,
    exportQRCodes,
    openSelectionRegen,
    openRegenForEquipment,
    closeRegenModal,
    regenerateSelectedQRCodes,
    exportFilteredList,
  };
}
